import type { EnvironmentState } from "@radar-sim/schema";

/**
 * Sea-clutter pattern generator. Returns a deterministic-by-(seed,simSeconds)
 * grid of intensities used by the renderer. Sea state increases base
 * intensity; relative wind (FROM-direction relative to own's heading) biases
 * toward upwind. STC suppresses inner-range clutter.
 */

function hash(...vals: number[]): number {
  let h = 2166136261 >>> 0;
  for (const v of vals) {
    h ^= Math.floor(v) >>> 0;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

function noise01(seed: number, i: number, j: number, t: number): number {
  return (hash(seed, i, j, Math.floor(t)) >>> 0) / 4294967296;
}

export interface ClutterCell {
  /** Bearing (deg true) of the cell centre. */
  bearingDeg: number;
  /** Range (nm) of the cell centre. */
  rangeNm: number;
  /** Intensity 0..1, post-STC suppression. */
  intensity: number;
}

export interface ClutterParams {
  /** Visible PPI range, nm. */
  rangeNm: number;
  environment: EnvironmentState;
  /** STC level (0=off, 1=full inner suppression). */
  stc: number;
  /** Stable seed for the scenario; same seed → same clutter. */
  seed: number;
  /** Sim time, seconds — gates pattern evolution. */
  simSeconds: number;
}

export function generateSeaClutter(params: ClutterParams): ClutterCell[] {
  const { rangeNm, environment, stc, seed, simSeconds } = params;
  const seaState = environment.seaState;
  if (seaState <= 0) return [];

  const baseIntensity = Math.min(1, seaState / 6);
  const cells: ClutterCell[] = [];

  // 36 bearings × 12 range bands. Inner band has highest intensity.
  for (let b = 0; b < 36; b++) {
    const bearingDeg = b * 10;
    // Wind bias: stronger clutter toward where wind is coming FROM.
    const windRel = ((bearingDeg - environment.windFromDeg + 540) % 360) - 180;
    const windFactor = 0.7 + 0.3 * Math.cos((windRel * Math.PI) / 180);
    for (let r = 1; r <= 12; r++) {
      const rangeNmCell = (r / 12) * rangeNm;
      const distanceFalloff = 1 / (1 + 0.5 * r);
      const stcMask =
        r < 4 ? Math.max(0, 1 - stc * (1 - r / 4)) : 1;
      const noise = noise01(seed, b, r, simSeconds / 5);
      const intensity =
        baseIntensity * windFactor * distanceFalloff * stcMask * noise;
      if (intensity > 0.03) {
        cells.push({ bearingDeg, rangeNm: rangeNmCell, intensity });
      }
    }
  }
  return cells;
}

/**
 * Modify target paint intensity based on the environment, gain/STC/FTC/tune
 * controls, and whether a rain cell sits between own and the target.
 */
export interface PaintAttenuation {
  gain: number;
  stc: number;
  ftc: number;
  tune: number;
  rangeNm: number;
  rainBlockingFraction: number;
}

export function attenuateTargetPaint(p: PaintAttenuation): number {
  const gain = Math.max(0, Math.min(1, p.gain));
  const tune = Math.max(0, Math.min(1, p.tune));
  // STC pulls down inner-range paint (anti-sea-clutter side effect on targets).
  const stcEffect = p.rangeNm < 1 ? 1 - p.stc * 0.4 : 1;
  // FTC reduces paint persistence — render-time effect; modelled here as a
  // mild paint reduction at all ranges proportional to setting.
  const ftcEffect = 1 - p.ftc * 0.2;
  const rainEffect = 1 - p.rainBlockingFraction * 0.7;
  const tuneEffect = 0.4 + 0.6 * tune;
  return Math.max(
    0,
    Math.min(1, gain * stcEffect * ftcEffect * rainEffect * tuneEffect),
  );
}
